/**
 * src/lib/api/agent.js — Chat persistence API (frontend reads via RLS)
 * Uses Supabase anon key — RLS scopes to auth.uid().
 */

import { supabase } from '../supabase';
import { apiFetch } from './client';
import { toConversationPatch } from '../agent/scopeContract';
import { logAppError } from '../errorReporter';

const API_AGENT = '/api/ai';

const MAX_UPLOAD_SIZE = 20 * 1024 * 1024; // 20 MB

/**
 * Upload a document to Supabase Storage and (optionally) trigger agent processing.
 * Uses direct Supabase upload to bypass Vercel 4.5 MB body limit.
 *
 * @param {{ file: File, projectId?: string, dealId?: string, userId?: string,
 *           skipProcessing?: boolean, documentType?: string, summary?: string }} params
 *   skipProcessing — file as a terminal 'processed' doc WITHOUT agent extraction
 *   (e.g. a doc generated FROM the brain; re-extracting it would echo back in).
 * @returns {{ documentId: string, processing: boolean }}
 */
export async function uploadDocument({ file, projectId, dealId, userId, skipProcessing = false, documentType, summary }) {
  if (file.size > MAX_UPLOAD_SIZE) {
    throw new Error(`File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum is 20 MB.`);
  }
  // Resolve the uploader if not supplied (the document generator has no userId prop).
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    userId = user.id;
  }

  // 1. Upload to Supabase Storage bucket 'project-documents'
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const storagePath = `${userId}/${Date.now()}-${safeName}`;
  // TICKET-658: ArrayBuffer, not File — storage-js wraps a Blob/File body in
  // FormData with an empty field name, which WebKit intermittently
  // mis-serializes on larger payloads ("No content provided" server-side).
  const { error: storageError } = await supabase.storage
    .from('project-documents')
    .upload(storagePath, await file.arrayBuffer(), { contentType: file.type, upsert: false });
  if (storageError) throw new Error('Upload failed: ' + storageError.message);

  // 2. Insert record into project_documents table
  const { data: docRow, error: insertError } = await supabase
    .from('project_documents')
    .insert({
      file_name: file.name,
      file_path: storagePath,
      file_size_bytes: file.size,
      mime_type: file.type,
      project_id: projectId || null,
      deal_id: dealId || null,
      uploaded_by: userId,
      // A generated doc needs no extraction → file it as terminal 'processed'.
      status: skipProcessing ? 'processed' : 'uploaded',
      ...(documentType ? { document_type: documentType } : {}),
      ...(summary ? { summary } : {}),
    })
    .select('id')
    .single();
  if (insertError) throw new Error('Failed to register document: ' + insertError.message);

  // 3. Trigger agent processing (fire-and-forget). SKIPPED for already-final docs
  //    (a doc generated FROM the brain — re-extracting it would echo into the brain).
  if (!skipProcessing) {
    apiFetch(API_AGENT, {
      method: 'POST',
      body: JSON.stringify({
        action: 'agent.process_document',
        document_id: docRow.id,
        project_id: projectId || undefined,
        deal_id: dealId || undefined,
      }),
    }).catch(err => console.warn('[uploadDocument] process error:', err.message));
  }

  return { documentId: docRow.id, processing: !skipProcessing };
}

/**
 * Fetch conversations for the current user, newest first.
 * @param {number} limit - max conversations to return
 */
export async function fetchConversations(limit = 20) {
  const { data, error } = await supabase
    .from('chat_conversations')
    .select('id, title, created_at, updated_at, project_id, deal_id')
    .is('archived_at', null)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) {
    // 2026-04-20 Phase 3 (audit P1-2): previously console.warn-only → the
    // CEO saw "chat sidebar empty" with no trail in app_error_logs. Now
    // every read failure is queryable via SQL: error_type='chat_fetch_error',
    // metadata->>'step'='fetchConversations'.
    console.warn('[agent-api] fetchConversations error:', error.message);
    logAppError({
      type: 'chat_fetch_error',
      message: `fetchConversations failed: ${error.message}`,
      view: 'AgentShell',
      metadata: { step: 'fetchConversations', code: error.code || null, limit },
    });
    return [];
  }
  return data || [];
}

/**
 * Fetch messages for a conversation, oldest first.
 * @param {string} conversationId
 */
export async function fetchMessages(conversationId) {
  const { data, error } = await supabase
    .from('chat_messages')
    .select('id, role, content, tool_calls, source, speaker_label, created_at')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });

  if (error) {
    // 2026-04-20 Phase 3 (audit P1-2): as with fetchConversations — turn the
    // silent swallow into a queryable row. When a conversation renders empty,
    // this log lets us see WHY in SQL instead of asking the CEO to open
    // devtools on mobile.
    console.warn('[agent-api] fetchMessages error:', error.message);
    logAppError({
      type: 'chat_fetch_error',
      message: `fetchMessages failed: ${error.message}`,
      view: 'AgentShell',
      metadata: { step: 'fetchMessages', code: error.code || null, conversationId },
    });
    return [];
  }
  return data || [];
}

/**
 * Create a new conversation (rarely needed — backend creates them).
 * @param {string|null} projectId
 */
export async function createConversation(projectId = null) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('chat_conversations')
    .insert({ user_id: user.id, project_id: projectId })
    .select('id, title, created_at')
    .single();

  if (error) throw new Error('[agent-api] createConversation failed: ' + error.message);
  return data;
}

/**
 * Update conversation's linked project.
 * @param {string} conversationId
 * @param {string|null} projectId
 */
export async function updateConversationProject(conversationId, projectId) {
  if (!conversationId) return null;

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ project_id: projectId || null, updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.warn('[agent-api] updateConversationProject error:', error.message);
    return null;
  }
  return data;
}

/**
 * Update conversation title.
 * @param {string} conversationId
 * @param {string} title
 */
export async function updateConversationTitle(conversationId, title) {
  if (!conversationId) return null;

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ title, updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.warn('[agent-api] updateConversationTitle error:', error.message);
    return null;
  }
  return data;
}

/**
 * Update conversation fields (title, project_id, deal_id, etc.).
 * @param {string} conversationId
 * @param {object} updates - fields to update
 */
export async function updateConversation(conversationId, updates) {
  if (!conversationId) return null;

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.warn('[agent-api] updateConversation error:', error.message);
    return null;
  }
  return data;
}

/**
 * Fetch conversations linked to a specific project.
 * @param {string} projectId
 * @param {number} limit
 */
export async function fetchConversationsByProject(projectId, limit = 50) {
  const { data, error } = await supabase
    .from('chat_conversations')
    .select('id, title, created_at, updated_at, project_id, deal_id')
    .eq('project_id', projectId)
    .is('archived_at', null)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[agent-api] fetchConversationsByProject error:', error.message);
    return [];
  }
  return data || [];
}

/**
 * Update conversation's linked deal.
 * @param {string} conversationId
 * @param {string|null} dealId
 */
export async function updateConversationDeal(conversationId, dealId) {
  if (!conversationId) return null;

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ deal_id: dealId || null, updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.warn('[agent-api] updateConversationDeal error:', error.message);
    return null;
  }
  return data;
}

/**
 * Fetch conversations linked to a specific deal.
 * @param {string} dealId
 * @param {number} limit
 */
export async function fetchConversationsByDeal(dealId, limit = 50) {
  const { data, error } = await supabase
    .from('chat_conversations')
    .select('id, title, created_at, updated_at, project_id, deal_id')
    .eq('deal_id', dealId)
    .is('archived_at', null)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[agent-api] fetchConversationsByDeal error:', error.message);
    return [];
  }
  return data || [];
}

/**
 * Soft-archive a conversation (never hard-delete).
 * @param {string} conversationId
 */
export async function deleteConversation(conversationId) {
  if (!conversationId) return null;

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ archived_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.warn('[agent-api] deleteConversation error:', error.message);
    return null;
  }
  return data;
}

/**
 * Restore a soft-archived conversation (clears archived_at).
 * Used by the sidebar "Archived" accordion restore action.
 * @param {string} conversationId
 */
export async function restoreConversation(conversationId) {
  if (!conversationId) return null;

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ archived_at: null, updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.warn('[agent-api] restoreConversation error:', error.message);
    return null;
  }
  return data;
}

/**
 * Update conversation's linked task (migration 087 column).
 * @param {string} conversationId
 * @param {string|null} taskId
 */
export async function updateConversationTask(conversationId, taskId) {
  if (!conversationId) return null;

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ task_id: taskId || null, updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.warn('[agent-api] updateConversationTask error:', error.message);
    return null;
  }
  return data;
}

/**
 * Update conversation's linked person (migration 087 column).
 * @param {string} conversationId
 * @param {string|null} personId - user_profiles.id
 */
export async function updateConversationPerson(conversationId, personId) {
  if (!conversationId) return null;

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ assigned_person_id: personId || null, updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    console.warn('[agent-api] updateConversationPerson error:', error.message);
    return null;
  }
  return data;
}

/**
 * Replace the full scope of a conversation. Normalizes the scope array via
 * scopeContract.toConversationPatch — splits entries into FK columns
 * (project_id, deal_id, task_id, assigned_person_id) and leftover freeform
 * `scope` jsonb for types not represented by an FK column.
 *
 * @param {{ conversationId: string, scope: Array<{type:string, id:string, name?:string}> }} params
 */
export async function updateScope({ conversationId, scope }) {
  if (!conversationId) return null;

  const patch = toConversationPatch(scope);

  const { data, error } = await supabase
    .from('chat_conversations')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .select()
    .single();

  if (error) {
    // 2026-04-20 — the Deal/Task/Person pill-won't-land bug is probably
    // an RLS/FK rejection here. Upgrade from console.warn → throw so
    // callers (useChat.persistScope) see the error + can log it to
    // app_error_logs with full context. Also log the patch itself so we
    // can see which FK column failed without having to reproduce.
    console.error('[agent-api] updateScope error:', error.message, {
      conversationId,
      patch: { project_id: patch.project_id, deal_id: patch.deal_id, task_id: patch.task_id, assigned_person_id: patch.assigned_person_id, overflow: (patch.scope || []).length },
    });
    const err = new Error(`updateScope failed: ${error.message}`);
    err.conversationId = conversationId;
    err.patch = patch;
    err.code = error.code;
    throw err;
  }
  return data;
}

/**
 * Append a message row to a conversation.
 * Used by the frontend chat shell to persist user turns optimistically and
 * assistant turns after the stream completes.
 *
 * Migration 089 added `source` ('text' | 'voice' | 'system') and
 * `speaker_label` (future multi-speaker). Defaults preserve existing callers.
 *
 * @param {{
 *   conversationId: string,
 *   role: 'user'|'assistant'|'system'|'tool',
 *   content: string,
 *   tool_calls?: object|null,
 *   source?: 'text'|'voice'|'system',
 *   speaker_label?: string|null,
 * }} params
 */
export async function appendMessage({
  conversationId,
  role,
  content,
  tool_calls = null,
  source = 'text',
  speaker_label = null,
}) {
  if (!conversationId) throw new Error('[agent-api] appendMessage: conversationId required');
  if (!role) throw new Error('[agent-api] appendMessage: role required');

  const { data, error } = await supabase
    .from('chat_messages')
    .insert({
      conversation_id: conversationId,
      role,
      content: content ?? '',
      tool_calls: tool_calls ?? null,
      source,
      speaker_label,
    })
    .select('id, role, content, tool_calls, source, speaker_label, created_at')
    .single();

  if (error) {
    console.warn('[agent-api] appendMessage error:', error.message);
    return null;
  }

  // Touch the conversation's updated_at so sidebar ordering reflects activity.
  supabase
    .from('chat_conversations')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', conversationId)
    .then(({ error: touchError }) => {
      if (touchError) console.warn('[agent-api] appendMessage touch error:', touchError.message);
    });

  return data;
}

/**
 * Fetch AI provider health summary — last 7 days of provider_error logs
 * aggregated per provider + error kind. Used by CEO dashboard widget.
 * Phase 88-01.
 *
 * @returns {Promise<{
 *   anthropic: { total: number, byKind: Record<string, number>, recent24h: number },
 *   openai: { total: number, byKind: Record<string, number>, recent24h: number },
 *   gemini: { total: number, byKind: Record<string, number>, recent24h: number },
 *   lastError: { provider, kind, at, latency_ms } | null,
 *   since: string
 * }>}
 */
export async function fetchProviderHealth() {
  try {
    const resp = await apiFetch('/api/ai?action=provider.health');
    return resp;
  } catch (err) {
    console.warn('[agent-api] fetchProviderHealth error:', err.message);
    // Graceful fallback so widget can render an "unknown" state
    return {
      anthropic: { total: 0, byKind: {}, recent24h: 0 },
      openai: { total: 0, byKind: {}, recent24h: 0 },
      gemini: { total: 0, byKind: {}, recent24h: 0 },
      lastError: null,
      _fetchError: err.message,
    };
  }
}
